import { useState } from "react";
import { LogIn, Play, RefreshCw, RotateCcw, type LucideIcon } from "lucide-react";
import {
  claudeSignInResultSchema,
  helpActionArgsSchemas,
  type GroomingQueueActionResult,
  type GroomingRepoStatus,
  type HelpAction,
  type HelpActionId,
  type ToolboxBridge,
} from "@ttc-spectree/toolbox-contract";
import { groomingErrorMessage } from "@/lib/grooming/error-messages";
import { plainBridgeError } from "@/lib/toolbox/bridge-error";
import type { GroomingQueueActions } from "@/lib/toolbox/queue";
import { cn } from "@/lib/utils";

/**
 * Confirm buttons in a Help answer (epic PI-SPEC-SW-E302, feature
 * PI-SPEC-SW-1557, ER-1655 Resolved Decision 1, frame H3).
 *
 * Claude stays read-only: an answer may name an action from the contract's
 * fixed list (`HELP_ACTIONS`); the person clicks, sees what will happen in a
 * short confirm in place, and only then the page runs the action it already
 * has (the same calls the Queue and Setup pages make). An action this page
 * has no handler for, or whose arguments do not parse, is never shown.
 */

/** What one confirmed action did, in plain words. */
export interface HelpActionOutcome {
  ok: boolean;
  message: string;
}

/** The page's handler for one action on the fixed list. */
export interface HelpActionHandler {
  icon: LucideIcon;
  /** The confirm button's word ("Retry"). */
  verb: string;
  /** What will happen, one or two plain sentences. */
  describe: (args: Record<string, unknown>) => string;
  run: (args: Record<string, unknown>) => Promise<HelpActionOutcome>;
}

export type HelpActionHandlers = Partial<Record<HelpActionId, HelpActionHandler>>;

const FAILED = "That didn't work. Try again in a moment.";

function queueOutcome(result: GroomingQueueActionResult, done: string): HelpActionOutcome {
  if (result.ok) return { ok: true, message: done };
  return {
    ok: false,
    message:
      result.errorCode === "UNKNOWN" && result.message.trim()
        ? result.message
        : groomingErrorMessage(result.errorCode).cause,
  };
}

function repoOutcome(
  statuses: readonly GroomingRepoStatus[],
  done: string,
  failed: string
): HelpActionOutcome {
  const stuck = statuses.find(
    (status) => status.state === "auth-required" || status.state === "error"
  );
  if (!stuck) return { ok: true, message: done };
  return { ok: false, message: stuck.message?.trim() ? stuck.message.trim() : failed };
}

function argString(args: Record<string, unknown>, key: string): string {
  const value = args[key];
  return typeof value === "string" ? value : "";
}

/**
 * The handlers this page has for the fixed action list, given the bridge and
 * the run queue. Queue actions need a contract-3 queue; repository actions
 * need the grooming toolset; Claude's sign-in works on every app. Pure apart
 * from the calls it binds; exported for tests.
 */
export function helpActionHandlersFor(
  bridge: ToolboxBridge | null,
  queue: { supported: boolean; actions: GroomingQueueActions }
): HelpActionHandlers {
  if (!bridge) return {};
  const handlers: HelpActionHandlers = {
    "sign-in-claude": {
      icon: LogIn,
      verb: "Sign in",
      describe: () =>
        "Claude's sign-in opens in your browser. Help and the queue are ready again once you are signed in.",
      run: async () => {
        if (typeof bridge.app.signInToClaude === "function") {
          const parsed = claudeSignInResultSchema.safeParse(await bridge.app.signInToClaude());
          if (parsed.success && parsed.data.ok)
            return { ok: true, message: "Signed in to Claude." };
          const reason = parsed.success ? (parsed.data.reason?.trim() ?? "") : "";
          return {
            ok: false,
            message: reason.length > 0 ? reason : "Claude's sign-in didn't finish. Try again.",
          };
        }
        await bridge.app.openClaudeSignIn();
        return { ok: true, message: "Claude's sign-in opened in your browser." };
      },
    },
  };
  if (queue.supported) {
    handlers["retry-queue-entry"] = {
      icon: RotateCcw,
      verb: "Retry",
      describe: () =>
        "It goes back into the queue, after the item that is running. If the problem is still there it stops again.",
      run: async (args) =>
        queueOutcome(await queue.actions.retry(argString(args, "entryId")), "Back in the queue."),
    };
    handlers["resume-queue"] = {
      icon: Play,
      verb: "Resume",
      describe: () => "The queue starts again with the next item waiting. Nothing else changes.",
      run: async () => queueOutcome(await queue.actions.resume(), "The queue is starting again."),
    };
  }
  const grooming = bridge.grooming;
  if (grooming) {
    handlers["update-repo-copies"] = {
      icon: RefreshCw,
      verb: "Update",
      describe: (args) =>
        `The Toolbox fetches the latest code for ${argString(args, "project")}. It can take a minute.`,
      run: async (args) =>
        repoOutcome(
          await grooming.refreshRepos(argString(args, "project")),
          "The project code is up to date.",
          "The project code could not be updated. Try again in a moment."
        ),
    };
    handlers["sign-in-repos"] = {
      icon: LogIn,
      verb: "Sign in",
      describe: (args) =>
        `The Toolbox runs the sign-in for ${argString(args, "project")}'s code. A sign-in window may open.`,
      run: async (args) =>
        repoOutcome(
          await grooming.signInToRepos(argString(args, "project")),
          "Signed in to the project's code.",
          "The sign-in to the project's code didn't work. Try again."
        ),
    };
  }
  return handlers;
}

/** The buttons an answer may show: on the fixed list, with valid arguments and a handler here. */
export function shownHelpActions(
  actions: readonly HelpAction[],
  handlers: HelpActionHandlers
): HelpAction[] {
  return actions.filter(
    (action) =>
      handlers[action.action] !== undefined &&
      helpActionArgsSchemas[action.action].safeParse(action.args).success
  );
}

const buttonClassName =
  "inline-flex h-[26px] items-center gap-1.5 whitespace-nowrap rounded-lg border border-[#27272A] bg-[#1A1A1E] px-[9px] text-xs font-medium text-[#FAFAFA] transition-colors hover:bg-[#222226] disabled:opacity-60";

/** One action button and, after a click, its confirm in place. */
export function HelpConfirmAction({
  action,
  handler,
}: {
  action: HelpAction;
  handler: HelpActionHandler;
}) {
  const [stage, setStage] = useState<"idle" | "confirm" | "running" | "done">("idle");
  const [outcome, setOutcome] = useState<HelpActionOutcome | null>(null);
  const Icon = handler.icon;

  const confirm = async () => {
    setStage("running");
    try {
      setOutcome(await handler.run(action.args));
    } catch (error) {
      setOutcome({ ok: false, message: plainBridgeError(error, FAILED) });
    }
    setStage("done");
  };

  return (
    <div className="flex flex-col gap-2" data-testid="help-action" data-action={action.action}>
      <button
        type="button"
        className={cn(buttonClassName, "self-start")}
        aria-expanded={stage === "confirm"}
        disabled={stage === "running"}
        onClick={() => {
          setOutcome(null);
          setStage("confirm");
        }}
      >
        <Icon className="h-[13px] w-[13px]" aria-hidden="true" />
        {action.label}
      </button>
      {(stage === "confirm" || stage === "running") && (
        <div
          role="group"
          aria-label={`Confirm: ${action.label}`}
          className="flex flex-col gap-2.5 rounded-[10px] border border-[rgba(35,164,255,0.35)] bg-[rgba(35,164,255,0.06)] p-3"
          data-testid="help-confirm"
        >
          <span className="text-[13px] font-semibold text-[#FAFAFA]">{action.label} now?</span>
          <span className="text-xs leading-[1.55] text-muted-foreground">
            {handler.describe(action.args)}
          </span>
          <div className="flex gap-2">
            <button
              type="button"
              className="inline-flex h-[26px] items-center rounded-lg bg-[#E11837] px-2.5 text-xs font-semibold text-white hover:bg-[#C8102E] disabled:opacity-60"
              disabled={stage === "running"}
              onClick={() => void confirm()}
            >
              {stage === "running" ? "Working…" : handler.verb}
            </button>
            <button
              type="button"
              className={buttonClassName}
              disabled={stage === "running"}
              onClick={() => {
                setStage("idle");
              }}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {stage === "done" && outcome && (
        <span
          role="status"
          className={cn("text-xs leading-[1.55]", outcome.ok ? "text-[#4ADE80]" : "text-[#FBBF24]")}
          data-testid="help-action-outcome"
        >
          {outcome.message}
        </span>
      )}
    </div>
  );
}
